import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
} from "react";

import type { ReactNode } from "react";
import type React from "react";

type ModalId = string;

export type ManagedModalComponentProps = {
  open: boolean;
  onClose: () => void;
};

interface ModalManagerState {
  registeredIds: ModalId[];
  stack: ModalId[];
}

type ModalAction =
  | { type: "REGISTER"; payload: { id: ModalId } }
  | { type: "UNREGISTER"; payload: { id: ModalId } }
  | { type: "OPEN"; payload: { id: ModalId } }
  | { type: "CLOSE"; payload: { id: ModalId } }
  | { type: "CLOSE_ALL" };

const modalReducer = (state: ModalManagerState, action: ModalAction): ModalManagerState => {
  switch (action.type) {
    case "REGISTER": {
      const { id } = action.payload;
      if (state.registeredIds.includes(id)) {
        return state;
      }
      return { ...state, registeredIds: [...state.registeredIds, id] };
    }
    case "UNREGISTER": {
      const { id } = action.payload;
      return {
        ...state,
        registeredIds: state.registeredIds.filter((registeredId) => registeredId !== id),
      };
    }
    case "OPEN": {
      if (
        !state.registeredIds.includes(action.payload.id) ||
        state.stack.includes(action.payload.id)
      ) {
        return state;
      }
      return { ...state, stack: [...state.stack, action.payload.id] };
    }
    case "CLOSE": {
      if (!state.stack.includes(action.payload.id)) return state;
      return { ...state, stack: state.stack.filter((id) => id !== action.payload.id) };
    }
    case "CLOSE_ALL": {
      if (state.stack.length === 0) return state;
      return { ...state, stack: [] };
    }
    default:
      return state;
  }
};

interface ModalContextType {
  modalStack: ModalId[];
  isModalOpen: (payload: { id: ModalId }) => boolean;
  open: (payload: { id: ModalId }) => void;
  close: (payload: { id: ModalId }) => void;
  closeAll: () => void;
  register: (payload: { id: ModalId }) => void;
  unregister: (payload: { id: ModalId }) => void;
  focus: (payload: { id: ModalId }) => void;
  setModalRef: (payload: { id: ModalId; el: HTMLElement | null }) => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [state, dispatch] = useReducer(modalReducer, {
    registeredIds: [],
    stack: [],
  });
  const modalRefs = useRef<Map<ModalId, HTMLElement>>(new Map());

  const register = useCallback<ModalContextType["register"]>(
    ({ id }) => dispatch({ type: "REGISTER", payload: { id } }),
    []
  );
  const unregister = useCallback<ModalContextType["unregister"]>(({ id }) => {
    dispatch({ type: "UNREGISTER", payload: { id } });
    modalRefs.current.delete(id);
  }, []);
  const open = useCallback<ModalContextType["open"]>(
    ({ id }) => dispatch({ type: "OPEN", payload: { id } }),
    []
  );
  const close = useCallback<ModalContextType["close"]>(
    ({ id }) => dispatch({ type: "CLOSE", payload: { id } }),
    []
  );
  const closeAll = useCallback<ModalContextType["closeAll"]>(
    () => dispatch({ type: "CLOSE_ALL" }),
    []
  );

  const isModalOpen = useCallback<ModalContextType["isModalOpen"]>(
    ({ id }) => state.stack.includes(id),
    [state.stack]
  );
  const modalStack = state.stack;

  const focus = useCallback<ModalContextType["focus"]>(({ id }) => {
    const modalElement = modalRefs.current.get(id);
    if (modalElement) {
      queueMicrotask(() => modalElement.focus());
    }
  }, []);

  const setModalRef = useCallback<ModalContextType["setModalRef"]>(({ id, el }) => {
    if (el) {
      modalRefs.current.set(id, el);
    } else {
      modalRefs.current.delete(id);
    }
  }, []);

  useEffect(() => {
    if (modalStack.length > 0) {
      const topModalId = modalStack[modalStack.length - 1];
      const topModalRef = modalRefs.current.get(topModalId);
      if (topModalRef && !topModalRef.contains(document.activeElement)) {
        focus({ id: topModalId });
      }
    }
  }, [modalStack, focus]);

  const contextValue = useMemo<ModalContextType>(
    () => ({
      modalStack,
      isModalOpen,
      open,
      close,
      closeAll,
      register,
      unregister,
      focus,
      setModalRef,
    }),
    [modalStack, isModalOpen, open, close, closeAll, register, unregister, focus, setModalRef]
  );

  return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>;
};

export const useModalManager = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModalManager must be used within a ModalProvider");
  }
  return context;
};

interface UseManagedModalOptions {
  id?: string;
  onClose?: () => void;
  ref?: React.Ref<HTMLElement>;
}

export interface ManagedModal {
  props: ManagedModalComponentProps;
  open: () => void;
  close: () => void;
  isOpen: boolean;
  id: ModalId;
}

export const useManagedModal = (options: UseManagedModalOptions = {}): ManagedModal => {
  const context = useModalManager();
  const generatedId = useId();
  const id = options.id ?? generatedId;

  useEffect(() => {
    context.register({ id });
    return () => context.unregister({ id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, context.register, context.unregister]);

  const onCloseRef = useRef(options.onClose);
  useEffect(() => {
    onCloseRef.current = options.onClose;
  });

  const open = useCallback(() => context.open({ id }), [context, id]);

  const close = useCallback(() => {
    context.close({ id });
    onCloseRef.current?.();
  }, [context, id]);

  const isOpen = context.isModalOpen({ id });

  const refCallback = useCallback(
    (el: HTMLElement | null) => {
      context.setModalRef({ id, el });
    },
    [context, id]
  );

  const modalProps = useMemo<ManagedModalComponentProps>(
    () => ({
      open: isOpen,
      onClose: close,
      ref: refCallback,
    }),
    [isOpen, close, refCallback]
  );

  return {
    id,
    props: modalProps,
    open,
    close,
    isOpen,
  };
};
